import './Theme'; // Import for Theme augmentation

import LinearProgress from '@mui/material/LinearProgress';
import { type Theme, useTheme } from '@mui/material/styles';
import { FiligranLoader } from 'filigran-icon';
import { type CSSProperties, type FunctionComponent } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    height: 'calc(100vh - 180px)',
    padding: '0 0 0 180px',
  },
  containerInElement: {
    width: '100%',
    height: '100%',
    display: 'table',
  },
  containerSizeXS: { width: 'auto' },
  loader: {
    width: '100%',
    margin: 0,
    padding: 0,
    position: 'absolute',
    top: '46%',
    left: 0,
    textAlign: 'center',
    zIndex: 20,
  },
  loaderInElement: {
    width: '100%',
    margin: 0,
    padding: 0,
    display: 'table-cell',
    verticalAlign: 'middle',
    textAlign: 'center',
  },
  '@keyframes ayradeBreathe': {
    '0%': {
      opacity: 0.45,
      transform: 'scale(0.97)',
    },
    '50%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '100%': {
      opacity: 0.45,
      transform: 'scale(0.97)',
    },
  },
  brand: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 24,
  },
  brandCompact: { gap: 12 },
  brandLogo: {
    width: 260,
    maxWidth: '60vw',
    height: 'auto',
    animation: 'ayradeBreathe 1.8s ease-in-out infinite',
  },
  brandLogoCompact: {
    width: 132,
    maxWidth: '70%',
  },
  brandProgress: {
    width: 260,
    maxWidth: '60vw',
    borderRadius: 2,
  },
  brandProgressCompact: {
    width: 132,
    maxWidth: '70%',
  },
}));

type LoaderVariant = 'inElement' | 'default';
type LoaderSize = 'xs' | 'sm' | 'md' | 'lg';

interface LoaderProps {
  variant?: LoaderVariant;
  withRightPadding?: boolean;
  size?: LoaderSize;
}

const Loader: FunctionComponent<LoaderProps> = ({
  variant = 'default',
  withRightPadding = false,
  size,
}) => {
  const { classes } = useStyles();
  const theme = useTheme<Theme>();

  const getContainer = (): string => {
    if (size === 'xs') {
      return classes.containerSizeXS;
    }
    if (variant === 'inElement') {
      return classes.containerInElement;
    }
    return classes.container;
  };

  const getSize = (): number => {
    if (size === 'xs') {
      return 16;
    }
    if (variant === 'inElement') {
      return 40;
    }
    return 80;
  };

  const containerStyle: CSSProperties = variant === 'inElement'
    ? { paddingRight: withRightPadding ? 200 : 0 }
    : {};

  const loaderStyle: CSSProperties = variant !== 'inElement'
    ? { paddingRight: withRightPadding ? 100 : 0 }
    : {};

  // Everything except the tiny inline case carries the product mark. Widgets on
  // a dashboard are large enough for it; a 16px spinner is not.
  const isBranded = size !== 'xs';
  const isCompact = variant === 'inElement';

  return (
    <div className={getContainer()} style={containerStyle}>
      <div
        className={variant === 'inElement' ? classes.loaderInElement : classes.loader}
        style={loaderStyle}
      >
        {isBranded
          ? (
              <div className={`${classes.brand} ${isCompact ? classes.brandCompact : ''}`}>
                <img
                  className={`${classes.brandLogo} ${isCompact ? classes.brandLogoCompact : ''}`}
                  src={theme?.logo ?? undefined}
                  alt="AYRADE Security Appliance"
                />
                <LinearProgress
                  className={`${classes.brandProgress} ${isCompact ? classes.brandProgressCompact : ''}`}
                />
              </div>
            )
          : <FiligranLoader height={getSize()} color={theme?.palette?.common?.grey} />}
      </div>
    </div>
  );
};

export default Loader;
